import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type SubscriptionFilterState = {
  searchTerm: string;
  status: string[];
  types: string[];
  plans: string[];
  methods: string[];
  dateFrom: string;
  dateTo: string;
  tenantIds: string[];
};

type SubscriptionFiltersProps = {
  filters: SubscriptionFilterState;
  onFiltersChange: (filters: SubscriptionFilterState) => void;
  statuses: string[];
  types: string[];
  planOptions: Array<{ id: string; label: string }>;
  methods: string[];
  counts?: {
    status: Record<string, number>;
    types: Record<string, number>;
    plans: Record<string, number>;
    methods: Record<string, number>;
    tenants?: Record<string, number>;
  };
  tenants: Array<{ id: string; label: string }>;
};

type TabType = "resumo" | "status" | "tipos" | "planos" | "metodos" | "periodo" | "clientes_saas";

export function SubscriptionFilters({
  filters,
  onFiltersChange,
  statuses,
  types,
  planOptions,
  methods,
  counts,
  tenants,
}: SubscriptionFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");
  const [tenantSearch, setTenantSearch] = useState("");

  const filterCount =
    filters.status.length +
    filters.types.length +
    filters.plans.length +
    filters.methods.length +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0);
    
  const tenantCount = filters.tenantIds.length;

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      status: [],
      types: [],
      plans: [],
      methods: [],
      dateFrom: "",
      dateTo: "",
      tenantIds: [],
    });
  };

  const removeFilterArrayItem = (
    type: keyof Pick<SubscriptionFilterState, "status" | "types" | "plans" | "methods" | "tenantIds">,
    value?: string,
  ) => {
    const current = (filters as any)[type] as string[];
    onFiltersChange({
      ...filters,
      [type]: value ? current.filter((v) => v !== value) : [],
    });
  };

  const removeDate = (type: "dateFrom" | "dateTo") => {
    onFiltersChange({ ...filters, [type]: "" });
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "status", label: "Status", icon: "📊" },
    { id: "tipos", label: "Tipos", icon: "🏷️" },
    { id: "planos", label: "Planos", icon: "💼" },
    { id: "metodos", label: "Métodos", icon: "💳" },
    { id: "periodo", label: "Período", icon: "📅" },
    { id: "clientes_saas", label: "Clientes SaaS", icon: "🏢" },
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "status": return filters.status.length;
      case "tipos": return filters.types.length;
      case "planos": return filters.plans.length;
      case "metodos": return filters.methods.length;
      case "periodo": return (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0);
      case "clientes_saas": return tenantCount;
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip open={!open ? undefined : false}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Filtre a listagem de assinaturas</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[520px] p-0" align="start">
        <div className="flex h-[420px]">
          <div className="w-52 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>

                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.status.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.status.map((s) => (
                            <Badge key={s} variant="secondary" className="gap-1 capitalize">
                              {s}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilterArrayItem("status", s)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.types.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipos</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.types.map((t) => (
                            <Badge key={t} variant="secondary" className="gap-1">
                              {t}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilterArrayItem("types", t)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.plans.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Planos</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.plans.map((p) => {
                            const plan = planOptions.find((po) => po.id === p);
                            return (
                              <Badge key={p} variant="secondary" className="gap-1">
                                {plan?.label || p}
                                <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilterArrayItem("plans", p)} />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {filters.methods.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Métodos</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.methods.map((m) => (
                            <Badge key={m} variant="secondary" className="gap-1">
                              {m}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilterArrayItem("methods", m)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {tenantCount > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Clientes SaaS</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.tenantIds.map((tid) => {
                            const t = tenants.find((x) => x.id === tid);
                            return (
                              <Badge key={tid} variant="secondary" className="gap-1">
                                {t?.label || tid}
                                <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilterArrayItem("tenantIds", tid)} />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(filters.dateFrom || filters.dateTo) && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Período</Label>
                        <div className="flex flex-wrap gap-2 mt-1 items-center">
                          {filters.dateFrom && (
                            <Badge variant="secondary" className="gap-1">
                              De {filters.dateFrom}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeDate("dateFrom")} />
                            </Badge>
                          )}
                          {filters.dateTo && (
                            <Badge variant="secondary" className="gap-1">
                              Até {filters.dateTo}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeDate("dateTo")} />
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={filters.status.includes(status)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            status: checked ? [...filters.status, status] : filters.status.filter((s) => s !== status),
                          });
                        }}
                      />
                      <Label htmlFor={`status-${status}`} className="capitalize flex-1 flex items-center gap-2">
                        {status}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.status?.[status] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "tipos" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Tipos de Assinatura</h3>
                <div className="space-y-2">
                  {types.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={filters.types.includes(type)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            types: checked ? [...filters.types, type] : filters.types.filter((t) => t !== type),
                          });
                        }}
                      />
                      <Label htmlFor={`type-${type}`} className="flex-1 flex items-center gap-2">
                        {type}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.types?.[type] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "planos" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Planos</h3>
                <div className="space-y-2">
                  {planOptions.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`plan-${plan.id}`}
                        checked={filters.plans.includes(plan.id)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            plans: checked ? [...filters.plans, plan.id] : filters.plans.filter((p) => p !== plan.id),
                          });
                        }}
                      />
                      <Label htmlFor={`plan-${plan.id}`} className="flex-1 flex items-center gap-2">
                        {plan.label}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.plans?.[plan.id] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "metodos" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Métodos de Pagamento</h3>
                <div className="space-y-2">
                  {methods.map((method) => (
                    <div key={method} className="flex items-center space-x-2">
                      <Checkbox
                        id={`method-${method}`}
                        checked={filters.methods.includes(method)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            methods: checked ? [...filters.methods, method] : filters.methods.filter((m) => m !== method),
                          });
                        }}
                      />
                      <Label htmlFor={`method-${method}`} className="flex-1 flex items-center gap-2">
                        {method}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.methods?.[method] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "clientes_saas" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Clientes SaaS</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Buscar clientes SaaS..."
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    className="w-full border rounded px-2 py-2 text-sm"
                  />
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {tenants
                      .filter((t) => t.label.toLowerCase().includes(tenantSearch.toLowerCase()))
                      .map((t) => (
                        <div key={t.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`tenant-${t.id}`}
                            checked={filters.tenantIds.includes(t.id)}
                            onCheckedChange={(checked) => {
                              onFiltersChange({
                                ...filters,
                                tenantIds: checked ? [...filters.tenantIds, t.id] : filters.tenantIds.filter((x) => x !== t.id),
                              });
                            }}
                          />
                          <Label htmlFor={`tenant-${t.id}`} className="flex-1 flex items-center gap-2">
                            {t.label}
                            <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.tenants?.[t.id] ?? 0}</Badge>
                          </Label>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "periodo" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Período de último pagamento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date-from" className="text-sm font-medium mb-2 block">De</Label>
                    <input
                      id="date-from"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value })}
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date-to" className="text-sm font-medium mb-2 block">Até</Label>
                    <input
                      id="date-to"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value })}
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                </div>
                {(filters.dateFrom || filters.dateTo) && (
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onFiltersChange({ ...filters, dateFrom: "", dateTo: "" })}>Limpar período</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
